import classNames from 'classnames';
import { memo, SetStateAction, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DepositBonusesIcon } from 'src/components/svg/DepositBonusesIcon';
import { Arrow } from 'src/components/svg/Arrows';
import { useModalContext } from 'src/hooks/useModalContext';
import {
  Bonus,
  ShortBalanceResponse,
  useGetUserBonuses,
  useScrollIntoView
} from 'react-easyrocket';
import { Skeletons } from 'src/components/Skeletons';
import { BonusCard } from './components/BonusCard';

interface DepositBonusesProps {
  shortBalance: ShortBalanceResponse;
  handleSelectBonus: (bonus: Bonus | null) => void;
  selectedBonus: Bonus | null;
  isModal: boolean;
  currencyIcon: string;
  showDepositBonuses: boolean;
  setShowDepositBonuses: (value: SetStateAction<boolean>) => void;
  isLoadingDepositMethods: boolean;
}

const _DepositBonuses = ({
  handleSelectBonus,
  selectedBonus,
  isModal,
  setShowDepositBonuses,
  showDepositBonuses,
  isLoadingDepositMethods
}: DepositBonusesProps) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { data, isLoading: isLoadingUserBonuses } = useGetUserBonuses({
    tags: 'standard',
    type: ['Deposits']
  });
  const { modalProps } = useModalContext<{ bonusId?: string }>();
  const bonusId = modalProps?.bonusId;
  const bonuses = data.slice(0.3);

  const bonusesListRef = useScrollIntoView({
    condition: showDepositBonuses && isModal,
    effectDependencies: [showDepositBonuses, isModal]
  });

  useEffect(() => {
    if (bonusId && !isLoadingUserBonuses && !isLoadingDepositMethods) {
      const selectedBonus = bonuses.find((bonus) => bonus.id === Number(bonusId));
      if (selectedBonus) {
        setShowDepositBonuses(true);
        handleSelectBonus?.(selectedBonus);
      }
    }
  }, [isLoadingUserBonuses, isLoadingDepositMethods, bonusId]);

  if (isLoadingUserBonuses) {
    return <Skeletons className="w-[160px] h-6" />;
  }

  if (bonuses?.length === 0) {
    return null;
  }

  return (
    <div className="w-full flex flex-col gap-x-2.5" ref={cardRef}>
      <div
        className="flex items-center gap-2.5 cursor-pointer"
        onClick={() => setShowDepositBonuses(!showDepositBonuses)}>
        <DepositBonusesIcon />
        <p className="font-bold text-sm text-dpip_bonus_title">{t('depositTab.Bonus')}</p>
        <Arrow
          fill="var(--dpip_bonus_title)"
          className={classNames(
            'w-[20px] stroke-dpip_bonus_title',
            showDepositBonuses ? 'rotate-180' : ''
          )}
        />
      </div>
      {showDepositBonuses && (
        <div
          className="w-full overflow-x-auto overflow-y-hidden mt-2.5 flex flex-row gap-x-4"
          ref={bonusesListRef}>
          {bonuses.map((bonus) => (
            <BonusCard
              bonus={bonus}
              selectedBonus={selectedBonus}
              handleSelectBonus={handleSelectBonus}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const DepositBonuses = memo(_DepositBonuses);
